// REST client. Session lives in an HttpOnly cookie; every request carries our CSRF header.
export class ApiError extends Error {
  status: number; code: string; issues: { path: string; message: string }[]; retryAfter: number | null;
  constructor(status: number, code: string, message: string, issues: ApiError["issues"] = [], retryAfter: number | null = null) {
    super(message); this.status = status; this.code = code; this.issues = issues; this.retryAfter = retryAfter;
  }
  field(name: string) { return this.issues.find((i) => i.path === name)?.message; }
}

let onUnauthorized: () => void = () => {};
export const setUnauthorizedHandler = (fn: () => void) => { onUnauthorized = fn; };

export async function api<T = any>(method: string, path: string, body?: unknown, opts: { signal?: AbortSignal } = {}): Promise<T> {
  const headers: Record<string, string> = { "x-majlis-client": "web" };
  if (body !== undefined) headers["content-type"] = "application/json";
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, { method, headers, body: body === undefined ? undefined : JSON.stringify(body), credentials: "same-origin", signal: opts.signal });
  } catch (e) {
    if ((e as Error).name === "AbortError") throw e;
    throw new ApiError(0, "network", "Can't reach the server. Check your connection.");
  }
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    if (res.status === 401 && !path.startsWith("/auth/login") && !path.startsWith("/auth/register")) onUnauthorized();
    const err = data?.error ?? { code: "http_" + res.status, message: res.statusText };
    throw new ApiError(res.status, err.code, err.message, err.issues ?? [], Number(res.headers.get("retry-after")) || null);
  }
  return data as T;
}

export const get = <T = any>(p: string, o?: { signal?: AbortSignal }) => api<T>("GET", p, undefined, o);
export const post = <T = any>(p: string, b: unknown = {}) => api<T>("POST", p, b);
export const patch = <T = any>(p: string, b: unknown = {}) => api<T>("PATCH", p, b);
export const put = <T = any>(p: string, b: unknown = {}) => api<T>("PUT", p, b);
export const del = <T = any>(p: string, b?: unknown) => api<T>("DELETE", p, b);

/** Upload with progress (fetch can't report upload progress). Resolves to the created attachment. */
export function upload(channelId: string, file: Blob, filename: string, onProgress: (f: number) => void, extra: Record<string, string> = {}):
  { promise: Promise<any>; abort: () => void } {
  const xhr = new XMLHttpRequest();
  const promise = new Promise<any>((resolve, reject) => {
    xhr.open("POST", `/api/v1/channels/${channelId}/attachments`);
    xhr.setRequestHeader("x-majlis-client", "web");
    xhr.setRequestHeader("content-type", file.type || "application/octet-stream");
    xhr.setRequestHeader("x-filename", encodeURIComponent(filename));
    for (const [k, v] of Object.entries(extra)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded / e.total); };
    xhr.onload = () => {
      let data: any = null; try { data = JSON.parse(xhr.responseText); } catch { /* empty */ }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data);
      else reject(new ApiError(xhr.status, data?.error?.code ?? "upload_failed", data?.error?.message ?? "Upload failed."));
    };
    xhr.onerror = () => reject(new ApiError(0, "network", "Upload failed. Check your connection."));
    xhr.onabort = () => reject(new ApiError(0, "aborted", "Upload cancelled."));
    xhr.send(file);
  });
  return { promise, abort: () => xhr.abort() };
}

/** PUT an image (avatar, banner, server icon) with an optional crop rectangle in source pixels. */
export async function putImage<T = any>(path: string, file: Blob, crop?: { x: number; y: number; w: number; h: number }): Promise<T> {
  const q = crop ? `?x=${crop.x}&y=${crop.y}&w=${crop.w}&h=${crop.h}` : "";
  let res: Response;
  try { res = await fetch(`/api/v1${path}${q}`, { method: "PUT", headers: { "x-majlis-client": "web", "content-type": file.type || "application/octet-stream" }, body: file, credentials: "same-origin" }); }
  catch { throw new ApiError(0, "network", "Can't reach the server. Check your connection."); }
  const data = await res.json().catch(() => null);
  if (!res.ok) { const e = data?.error ?? { code: "http_" + res.status, message: res.statusText }; throw new ApiError(res.status, e.code, e.message); }
  return data as T;
}
